import { useEffect, useState, useRef, useMemo, use } from "react";
import { select, useDispatch } from "@wordpress/data";


import {
    checkAndGetDefaultFieldName,
    hideFieldByName,
    getDefaultFieldsName
} from "./helper-fronted";
import { resolveDefaultDateFromField, formatStoredDateForDatepicker } from './datepicker-config';
import {
    hasInputMask,
    isInputMaskValueValid,
    getInputMaskValidationErrorMessage,
} from './inputmask-config';
import { generateTimeSlots, getTimepickerConfig, resolveLinkedDateValue } from './timepicker-config';

const HIDDEN_CLASS = 'wc-optional-hidden';
let currentUserRoles = fme_upload_data.user_roles || [];
const store = select("wc/store/cart");
const cartDispatch = wp.data.dispatch('wc/store/cart');
const cart = store.getCartData();

const debounceTimers = { current: {} };
const sentHashes = { current: {} };


const fieldFeesDataOnLoad = {
    fieldName: '',
    fieldValue: '',
    fieldFees: '',
    fieldType: ''
};

// Remove required attribute and prevent validation for optional fields
export const removeRequiredAttributesAndPreventValidation = (allFields) => {

    useEffect(() => {
        const cleanupFunctions = [];

        const makeFieldOptional = (input) => {
            if (!input) return;

            // Force remove required every time
            input.removeAttribute('required');
            input.removeAttribute('aria-required');
            input.required = false; // also set the property

            // Clear any validation message
            input.setCustomValidity('');

            // Prevent browser popup even if required gets added back
            const preventInvalid = (e) => {
                e.preventDefault();
                input.setCustomValidity('');
            };

            input.addEventListener('invalid', preventInvalid);

            // Extra protection: remove required on every interaction
            const forceRemoveOnInteraction = () => {
                input.removeAttribute('required');
                input.required = false;
                input.setCustomValidity('');
            };

            // These events fire very often in WooCommerce checkout
            ['focus', 'blur', 'input', 'change'].forEach(event => {
                input.addEventListener(event, forceRemoveOnInteraction);
            });

            // Store cleanups
            cleanupFunctions.push(() => {
                input.removeEventListener('invalid', preventInvalid);
                ['focus', 'blur', 'input', 'change'].forEach(event => {
                    input.removeEventListener(event, forceRemoveOnInteraction);
                });
            });
        };

        // Initial pass + setup watchers
        allFields.forEach(field => {
            const fieldName = checkAndGetDefaultFieldName(field.field_name);
            if (fieldName === 'not_default') return;

            if (field.is_required == 0) {
                const input = document.getElementById(fieldName);
                if (!input) return;
                makeFieldOptional(input);
            }
        });

        // Cleanup everything
        return () => {
            cleanupFunctions.forEach(cleanup => cleanup());
        };
    }, [allFields]);

};

// Hide a checkout field by its name
export const hideField = (fieldName, field) => {
    if (!fieldName || !field) return;

    let $container = null;

    // 1. Special case: Order notes / Additional information
    if (field.field_name === 'order_comments') {
        $container = jQuery('.wc-block-checkout__add-note');
    }
    // 2. Additional fields section (custom ones)
    else if (field.type === 'additional') {
        $container = jQuery(`.${fieldName}, #${fieldName}`).closest('.wc-block-components-address-form__field, .wc-block-checkout__field');
    }
    // 3. Billing / Shipping fields (most common case)
    else if (fieldName.startsWith('billing-') || fieldName.startsWith('shipping-')) {
        const prefix = fieldName.startsWith('billing-') ? 'billing' : 'shipping';
        const fieldKey = fieldName.replace(prefix + '-', '');

        $container = jQuery(`#${fieldName}`)
            .closest(
                `.wc-block-components-address-form__${fieldKey}, ` +
                `.wc-block-components-address-form__field, ` +
                `.wc-block-checkout__${prefix}-address-fields`
            );
    }

    if (!$container || !$container.length) {
        // Last resort fallback - try to find by id closest reasonable parent
        $container = jQuery(`#${fieldName}`).closest(
            '.wc-block-components-text-input, ' +
            '.wc-block-components-select-input, ' +
            '.wc-block-components-textarea-input, ' +
            '.wc-block-components-address-form__field, ' +
            '.wc-block-checkout__field'
        );
    }

    if ($container && $container.length) {
        // Hide container
        $container.hide();
        $container.addClass(HIDDEN_CLASS);

        // Find the actual input/select/textarea
        const $input = $container.find('input, select, textarea').first();

        if ($input.length) {
            // Remove required + validation
            $input
                .removeAttr('required')
                .removeAttr('aria-required')
                .prop('required', false);

            // Clear value (optional but recommended for hidden fields)
            if ($input.is('select')) {
                $input.val('').trigger('change');
            } else {
                $input.val('0').trigger('input').trigger('change');
            }

            // Clean up validation messages
            if ($container.length > 0) {
                $container
                    .removeClass('has-error')
                    .find('.wc-block-components-validation-error')
                    .remove();
            }
        }
    }
}

// ────────────────────────────────────────────────────────────────
// Hook to hide checkout fields based on conditions
export const hideShowDefaultFieldsBasedOnConditions = (allFields) => {
    useEffect(() => {
        const cleanup = [];

        // Hide all optional (but marked required in your logic?) fields initially
        allFields.forEach((field) => {
            const fieldName = checkAndGetDefaultFieldName(field.field_name);
            if (fieldName === 'not_default') return;

            // Decide whether to hide
            // Change condition according to your real business logic!
            let shouldHide = field.is_enable == 0; // ← most common case
            shouldHide = !checkUserRoleConditions(field); // check user roles conditions
            shouldHide = !checkProductConditionsForField(field); // check product/category conditions


            if (shouldHide) {
                hideField(fieldName, field);

                // Re-hide after checkout updates / country change etc.
                const tryRehide = () => {
                    setTimeout(() => {
                        hideField(fieldName, field);
                    }, 50); // tiny delay helps after DOM mutation
                };

                const events = [
                    'updated_checkout',
                    'country_to_state_changed',
                    'updated_shipping_method',
                    'render_checkout_form', // sometimes needed
                ];

                events.forEach((eventName) => {
                    document.addEventListener(eventName, tryRehide);
                    cleanup.push(() => document.removeEventListener(eventName, tryRehide));
                });
            }
        });

        // Initial hide attempt after small delay (WC blocks are async)
        const initialHide = setTimeout(() => {
            allFields.forEach((field) => {
                const fieldName = checkAndGetDefaultFieldName(field.field_name);
                if (fieldName !== 'not_default') {
                    // hideFieldByName(fieldName, field);
                }
            });
        }, 300);

        // Cleanup
        return () => {
            cleanup.forEach((fn) => fn());
            clearTimeout(initialHide);
        };
    }, [allFields]);
}


// check user roles conditions for a field
export const checkUserRoleConditions = (field) => {
    const userRoles = currentUserRoles || [];
    const specificRoles = field.specific_user_role || [];

    let roleCheckPassed = true;
    // === User Role Check ===
    if (field.userrole_check === "fme_display_user_role") {
        if (specificRoles.length > 0) {
            roleCheckPassed = userRoles.some((role) => specificRoles.includes(role));
        }
    } else if (field.userrole_check === "fme_hide_user_role") {
        if (specificRoles.length > 0) {
            roleCheckPassed = !userRoles.some((role) => specificRoles.includes(role));
        }
    }

    return roleCheckPassed;
}

const FME_CATEGORY_KEY = 'Category ID';
const FME_TAG_KEY = 'Tag ID';
const FME_PRODUCT_ID_KEY = 'Product ID';
const FME_VARIATION_ID_KEY = 'Variation ID';

export const getCartItemProductIds = (cartItem) => {
    const ids = new Set([Number(cartItem.id)]);
    if (Array.isArray(cartItem.item_data)) {
        cartItem.item_data.forEach((data) => {
            if (data.key === FME_PRODUCT_ID_KEY || data.key === FME_VARIATION_ID_KEY) {
                ids.add(Number(data.value));
            }
        });
    }
    return Array.from(ids).filter(Boolean);
};

export const getCartItemTermIds = (cartItem, termKey) => {
    const termIds = new Set();
    if (Array.isArray(cartItem.item_data)) {
        cartItem.item_data.forEach((data) => {
            if (data.key === termKey) {
                termIds.add(Number(data.value));
            }
        });
    }
    return Array.from(termIds);
};

const cartItemMatchesProductIds = (cartItem, productIds) => {
    const normalizedProductIds = productIds.map((id) => Number(id));
    const cartProductIds = getCartItemProductIds(cartItem);
    return cartProductIds.some((id) => normalizedProductIds.includes(id));
};

const cartHasMatchingTerm = (cartItems, termKey, termIds) => {
    const normalizedTermIds = termIds.map((id) => String(id));
    const cartTermIds = new Set();
    cartItems.forEach((cartItem) => {
        getCartItemTermIds(cartItem, termKey).forEach((id) => cartTermIds.add(String(id)));
    });
    return Array.from(cartTermIds).some((id) => normalizedTermIds.includes(id));
};

// check product/category/tag conditions for a field
export const checkProductConditionsForField = (field) => {

    const cartItems = store.getCartData().items;
    let productCategoryCheckPassed = true;
    // === Product/Category/Tag Check ===
    if (field.ischeckpc !== "") {
        if (field.ischeckpc === "display") {
            if (field.specific_pc === "product") {

                const productIds = Array.isArray(field.selected_pc) ? field.selected_pc : (field.selected_pc ? [field.selected_pc] : []);

                // If productIds is empty, consider it as "all products"
                if (productIds.length === 0) {
                    productCategoryCheckPassed = true;
                } else {
                    productCategoryCheckPassed = cartItems.some((cartItem) => cartItemMatchesProductIds(cartItem, productIds));
                }
            } else if (field.specific_pc === "category") {
                const categoryIds = field.selected_pc || [];
                // If categoryIds is empty, consider it as "all categories"
                if (categoryIds.length === 0) {
                    productCategoryCheckPassed = true;
                } else {
                    productCategoryCheckPassed = cartHasMatchingTerm(cartItems, FME_CATEGORY_KEY, categoryIds);
                }
            } else if (field.specific_pc === "tag") {
                const tagIds = field.selected_pc || [];
                if (tagIds.length === 0) {
                    productCategoryCheckPassed = true;
                } else {
                    productCategoryCheckPassed = cartHasMatchingTerm(cartItems, FME_TAG_KEY, tagIds);
                }
            }
        } else if (field.ischeckpc === "hide") {
            if (field.specific_pc === "product") {
                const productIds = Array.isArray(field.selected_pc) ? field.selected_pc : (field.selected_pc ? [field.selected_pc] : []);
                if (productIds.length === 0) {
                    productCategoryCheckPassed = true;
                } else {
                    productCategoryCheckPassed = !cartItems.some((cartItem) => cartItemMatchesProductIds(cartItem, productIds));
                }

            } else if (field.specific_pc === "category") {
                const categoryIds = field.selected_pc || [];
                if (categoryIds.length === 0) {
                    productCategoryCheckPassed = true;
                } else {
                    productCategoryCheckPassed = !cartHasMatchingTerm(cartItems, FME_CATEGORY_KEY, categoryIds);
                }
            } else if (field.specific_pc === "tag") {
                const tagIds = field.selected_pc || [];
                if (tagIds.length === 0) {
                    productCategoryCheckPassed = true;
                } else {
                    productCategoryCheckPassed = !cartHasMatchingTerm(cartItems, FME_TAG_KEY, tagIds);
                }
            }
        }
    }

    return productCategoryCheckPassed;
}


export const isValidPhone = (phone) => {
    if (!phone) return true; // allow empty if not required

    // Remove all non-digits except + at beginning
    const cleaned = phone.replace(/[^\d+]/g, '');

    // Very common formats people actually use
    return (
        // +1234567890
        /^[+]?[0-9]{8,15}$/.test(cleaned) ||
        // 1234567890 or 03451234567 (local)
        /^[0-9]{8,15}$/.test(cleaned)
    );
};

/**
 * Validates a password against security requirements.
 * 
 * @param {string} password - The password string to validate
 * @returns {boolean} True if password is empty/falsy OR meets all security requirements, false otherwise
 * 
 * @description
 * Password must contain ALL of the following:
 * - Minimum 8 characters in length
 * - At least 1 lowercase letter (a-z)
 * - At least 1 uppercase letter (A-Z)
 * - At least 1 numeric digit (0-9)
 * - At least 1 special character from: @ $ ! % * ? &
 * 
 */
export const isValidPassword = (password) => {
    if (!password) return true; // allow empty if not required

    // Password must contain: min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
    const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;


    return pattern.test(password);
};

// Validates a URL using a regex pattern
export const isValidURL = (url) => {
    if (!url) return true; // allow empty if not required

    // Validate standard URL formats such as https://example.com or http://example.com/path
    try {
        const parsedUrl = new URL(url);
        return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch (error) {
        return false;
    }
};

export const isValidEmail = (email) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
};

export const isInlineValidationEnabled = () => {
    return !!(typeof fme_upload_data !== 'undefined' && fme_upload_data?.inline_validation_enabled);
};

/**
 * Whether a field has an assigned custom regex validator (mask takes precedence elsewhere).
 *
 * @param {Object} field Field row.
 * @returns {boolean}
 */
export const hasCustomValidator = (field) => {
    return !!(field?.custom_validator_id);
};

/**
 * Assigned built-in validator key (email|url|tel|password), or empty.
 *
 * @param {Object} field Field row.
 * @returns {string}
 */
export const getBuiltinValidatorId = (field) => {
    const id = field?.builtin_validator_id ? String(field.builtin_validator_id) : '';
    return ['email', 'url', 'tel', 'password'].includes(id) ? id : '';
};

/**
 * Whether custom, confirm, or built-in assignment replaces automatic type defaults.
 *
 * @param {Object} field Field row.
 * @returns {boolean}
 */
export const hasExplicitValidationRule = (field) => {
    return !!(field?.custom_validator_id || field?.confirm_validator_id || getBuiltinValidatorId(field));
};

/**
 * Whether a format rule should apply (explicit built-in or automatic type default when None).
 *
 * @param {Object} field Field row.
 * @param {string} ruleKey email|url|tel|password.
 * @returns {boolean}
 */
export const shouldApplyTypeFormatRule = (field, ruleKey) => {
    if (!field || hasInputMask(field)) {
        return false;
    }
    if (field?.custom_validator_id || field?.confirm_validator_id) {
        return false;
    }
    const assigned = getBuiltinValidatorId(field);
    if (assigned) {
        return assigned === ruleKey;
    }
    return field?.field_type === ruleKey;
};

export const getBuiltinValidatorErrorMessage = (ruleKey, t = null) => {
    const translate = typeof t === 'function'
        ? t
        : (key, fallback) => fallback;

    switch (ruleKey) {
        case 'email':
            return translate('email_validation_error', __('Please enter a valid email address', 'conditional-checkout-fields-for-woocommerce'));
        case 'url':
            return translate('url_validation_error', __('Please enter a valid URL', 'conditional-checkout-fields-for-woocommerce'));
        case 'tel':
            return translate('phone_validation_error', __('Please enter a valid phone number (8-15 digits, + optional)', 'conditional-checkout-fields-for-woocommerce'));
        case 'password':
            return translate('password_validation_error', __('Password must contain at least 8 characters, 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character (@$!%*?&)', 'conditional-checkout-fields-for-woocommerce'));
        default:
            return __('This value is not valid.', 'conditional-checkout-fields-for-woocommerce');
    }
};

export const isValidBuiltinValidator = (value, ruleKey) => {
    switch (ruleKey) {
        case 'email':
            return isValidEmail(value);
        case 'url':
            return isValidURL(value);
        case 'tel':
            return isValidPhone(value);
        case 'password':
            return isValidPassword(value);
        default:
            return true;
    }
};

export const getCustomValidatorsMap = () => {
    return (typeof fme_upload_data !== 'undefined' && fme_upload_data?.custom_validators)
        ? fme_upload_data.custom_validators
        : {};
};

export const getConfirmValidatorsMap = () => {
    return (typeof fme_upload_data !== 'undefined' && fme_upload_data?.confirm_validators)
        ? fme_upload_data.confirm_validators
        : {};
};

export const getConfirmSourceValue = (confirmFieldName, sourceField, allFieldValues) => {
    if (!sourceField || !allFieldValues) {
        return '';
    }

    if (Object.prototype.hasOwnProperty.call(allFieldValues, sourceField)) {
        return allFieldValues[sourceField];
    }

    const match = String(confirmFieldName).match(/^(.+)_(\d+)$/);
    if (match) {
        const suffixedSource = `${sourceField}_${match[2]}`;
        if (Object.prototype.hasOwnProperty.call(allFieldValues, suffixedSource)) {
            return allFieldValues[suffixedSource];
        }
    }

    return '';
};

export const isValidConfirmValidator = (value, field, allFieldValues) => {
    if (!field?.confirm_validator_id) {
        return true;
    }

    if (value === null || value === undefined || String(value).trim() === '') {
        return true;
    }

    const validators = getConfirmValidatorsMap();
    const validator = validators[field.confirm_validator_id];
    if (!validator?.source_field) {
        return true;
    }

    const sourceValue = getConfirmSourceValue(field.field_name, validator.source_field, allFieldValues);
    return String(sourceValue) === String(value);
};

export const getConfirmValidatorErrorMessage = (field) => {
    if (!field?.confirm_validator_id) {
        return '';
    }

    const validators = getConfirmValidatorsMap();
    const validator = validators[field.confirm_validator_id];
    if (!validator) {
        return __('Values do not match.', 'conditional-checkout-fields-for-woocommerce');
    }

    let message = validator.message || __('Values do not match.', 'conditional-checkout-fields-for-woocommerce');
    if (message.includes('%s')) {
        const sourceLabel = validator.source_field_label || validator.source_field || '';
        message = message.replace('%s', sourceLabel);
    }

    return message;
};

export const parsePhpRegexPattern = (pattern) => {
    if (!pattern) {
        return null;
    }

    const trimmed = String(pattern).trim();
    const delimiter = trimmed.charAt(0);
    const delimiters = ['/', '#', '~'];

    if (!delimiters.includes(delimiter)) {
        try {
            return new RegExp(trimmed);
        } catch (error) {
            return null;
        }
    }

    const lastIndex = trimmed.lastIndexOf(delimiter);
    if (lastIndex <= 0) {
        return null;
    }

    const body = trimmed.slice(1, lastIndex);
    const flags = trimmed.slice(lastIndex + 1);

    try {
        return new RegExp(body, flags);
    } catch (error) {
        return null;
    }
};

export const isValidCustomValidator = (value, field) => {
    if (!value || !field?.custom_validator_id) {
        return true;
    }

    const validators = getCustomValidatorsMap();
    const validator = validators[field.custom_validator_id];
    if (!validator?.pattern) {
        return true;
    }

    const regex = parsePhpRegexPattern(validator.pattern);
    if (!regex) {
        return true;
    }

    return regex.test(String(value));
};

export const getCustomValidatorErrorMessage = (field) => {
    if (!field?.custom_validator_id) {
        return '';
    }

    const validators = getCustomValidatorsMap();
    const validator = validators[field.custom_validator_id];
    return validator?.message || __('This value is not valid.', 'conditional-checkout-fields-for-woocommerce');
};

/**
 * Customer-facing format hint for an assigned custom validator (mask-style "Format: …").
 *
 * @param {Object} field Field row from localized block data.
 * @returns {string}
 */
export const getCustomValidatorFormatHint = (field) => {
    if (!field || hasInputMask(field)) {
        return '';
    }

    if (field.custom_validator_format_hint) {
        return field.custom_validator_format_hint;
    }

    if (!field.custom_validator_id) {
        return '';
    }

    const validators = getCustomValidatorsMap();
    const validator = validators[field.custom_validator_id];
    if (!validator) {
        return '';
    }

    if (validator.formatHint) {
        return validator.formatHint;
    }

    if (validator.example) {
        const template = (typeof fme_upload_data !== 'undefined' && fme_upload_data?.i18n?.input_mask_format_simple)
            ? fme_upload_data.i18n.input_mask_format_simple
            : 'Format: %s';
        return template.replace('%s', validator.example);
    }

    return '';
};

/**
 * Format hint for assigned built-in (email/url/tel) when no custom hint is present.
 *
 * @param {Object} field Field row.
 * @returns {string}
 */
export const getBuiltinValidatorFormatHint = (field) => {
    if (!field || hasInputMask(field) || field?.custom_validator_id) {
        return '';
    }

    if (field.custom_validator_format_hint && getBuiltinValidatorId(field)) {
        return field.custom_validator_format_hint;
    }

    const builtinId = getBuiltinValidatorId(field);
    const examples = {
        email: 'name@example.com',
        url: 'https://example.com',
        tel: '+1234567890',
    };
    const example = examples[builtinId];
    if (!example) {
        return '';
    }

    const template = (typeof fme_upload_data !== 'undefined' && fme_upload_data?.i18n?.input_mask_format_simple)
        ? fme_upload_data.i18n.input_mask_format_simple
        : 'Format: %s';
    return template.replace('%s', example);
};

export { hasInputMask, isInputMaskValueValid, getInputMaskValidationErrorMessage };

/**
 * Mask-first format validation: mask, then regex, then confirm (mutually exclusive with mask).
 * Mask checks always run when a mask is configured.
 *
 * @returns {{ valid: boolean, message: string }}
 */
export const validateFieldFormatRules = (value, field, allFieldValues = {}) => {
    const trimmed = value !== null && value !== undefined ? String(value).trim() : '';
    if (!trimmed) {
        return { valid: true, message: '' };
    }

    if (hasInputMask(field)) {
        if (!isInputMaskValueValid(value, field)) {
            return { valid: false, message: getInputMaskValidationErrorMessage(field) };
        }
        return { valid: true, message: '' };
    }

    if (field?.custom_validator_id && !isValidCustomValidator(value, field)) {
        return { valid: false, message: getCustomValidatorErrorMessage(field) };
    }

    if (field?.confirm_validator_id && !isValidConfirmValidator(value, field, allFieldValues)) {
        return { valid: false, message: getConfirmValidatorErrorMessage(field) };
    }

    const builtinId = getBuiltinValidatorId(field);
    if (builtinId && !isValidBuiltinValidator(value, builtinId)) {
        return { valid: false, message: getBuiltinValidatorErrorMessage(builtinId) };
    }

    // Automatic type defaults when None (no explicit assignment).
    if (!hasExplicitValidationRule(field)) {
        const type = field?.field_type;
        if (type === 'email' && !isValidEmail(value)) {
            return { valid: false, message: getBuiltinValidatorErrorMessage('email') };
        }
        if (type === 'url' && !isValidURL(value)) {
            return { valid: false, message: getBuiltinValidatorErrorMessage('url') };
        }
        if (type === 'tel' && !isValidPhone(value)) {
            return { valid: false, message: getBuiltinValidatorErrorMessage('tel') };
        }
        if (type === 'password' && !isValidPassword(value)) {
            return { valid: false, message: getBuiltinValidatorErrorMessage('password') };
        }
    }

    return { valid: true, message: '' };
};

export const getCheckboxLabel = (label) => {
    // If label already contains HTML tags, return as is
    const htmlTagPattern = /<[^>]+>/;
    if (htmlTagPattern.test(label)) {
        return label;
    }

    const linkPattern = /\{link,\s*href="([^"]+)",\s*link_text="([^"]+)"\}/g;

    let processedLabel = label;

    // Replace all occurrences of the link pattern with anchor tags
    processedLabel = processedLabel.replace(linkPattern, (match, href, linkText) => {
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
    });

    // If no link pattern was found, escape special characters to prevent HTML injection
    if (processedLabel === label) {
        const div = document.createElement('div');
        div.textContent = label;
        return div.innerHTML;
    }

    return processedLabel;
};



export const getCheckboxLabelText = (label, suffix = '') => {
    // Extract link_text value from {link, href="...", link_text="..."} pattern
    const linkPattern = /\{link,\s*href="[^"]*",\s*link_text="([^"]+)"\}/;
    const match = linkPattern.exec(label);

    if (match && match[1]) {

        return match[1] + ' ' + suffix;
    }

    return label;
};


export const checkedDefaultOptionsOnLoad = (customFields, fieldValues, setFieldValues, forSpecificField = null) => {

    const results = [];

    customFields.forEach((field) => {

        if (field.is_enable == 0 || field.isHide === true || (null != forSpecificField && field.field_name != forSpecificField)) {
            return;
        }

        // Handle field repetition
        const shouldRepeat = field.repeat_rules?.fme_is_repeat == 'on';
        let repeatCount = shouldRepeat ? getRepeatCount(cart, field.repeat_rules) : 1;
        if (0 == repeatCount) {
            repeatCount = 1;
        }

        [...Array(repeatCount)].forEach((_, index) => {
            const isOriginal = index === 0;
            const fieldIndex = index + 1;

            let isRepeatPrice = field.repeat_rules.fme_is_repeat == 'on' && field.repeat_rules.fme_repeat_price != 'on';
            let repeatPrice = !isOriginal && isRepeatPrice ? false : true;

            const suffix = field.repeat_rules?.fme_repeat_label_suffix === 'alphabet'
                ? String.fromCharCode(65 + index)
                : (fieldIndex).toString();
            const fieldName = isOriginal ? field.field_name : `${field.field_name}_${fieldIndex}`;

            if (field.field_type === 'radio' || field.field_type === 'select' || field.field_type === 'multiselect' || field.field_type === 'checkbox_group') {
                const groupName = fieldName;
                const options = field.options || [];
                const normalizeGroupKey = (key) => String(key ?? '').replace(/\s/g, '');

                if (field.field_type === 'checkbox_group') {
                    const defaultSelections = [];

                    options.forEach((option) => {
                        const [label, value, price = '', isDefault = ''] = option;
                        if ('' !== isDefault && normalizeGroupKey(value) === normalizeGroupKey(isDefault)) {
                            defaultSelections.push({
                                key: value,
                                value: !repeatPrice ? 0 : (price || 0),
                            });
                        }
                    });

                    if (defaultSelections.length > 0) {
                        let feeName = field.field_label ? field.field_label : 'Checkbox Group';
                        if (!isOriginal) {
                            feeName += `(${suffix})`;
                        }

                        setFieldValues((prevValues) => ({
                            ...prevValues,
                            [groupName]: defaultSelections,
                            [groupName + '_data']: field,
                        }));

                        if (!repeatPrice && fieldIndex > 1) {
                            return true;
                        }

                        results.push({
                            fieldName: fieldName,
                            feeName: feeName,
                            fieldValue: defaultSelections,
                            fieldFees: defaultSelections.reduce((sum, item) => sum + Number(item.value || 0), 0),
                            priceType: getFieldPriceType(field),
                            ...getSessionDynamicPriceFields(field),
                            fieldType: field.field_type,
                            fieldTax: field.is_taxable || '0'
                        });
                    }
                } else {
                    options.forEach((option) => {
                        const [label, value, price = '', isDefault = ''] = option;

                        if ('' !== isDefault && value === isDefault) {

                            let feeName = field.field_label ? field.field_label : 'Checkbox';
                            if (!isOriginal) {
                                feeName += `(${suffix})`;
                            }

                            setFieldValues((prevValues) => ({
                                ...prevValues,
                                [groupName]: value,
                                [groupName + '_data']: field,
                            }));

                            if (!repeatPrice && fieldIndex > 1) {
                                return true; // skip adding fee for repeated fields if repeatPrice is false
                            }

                            results.push({
                                fieldName: fieldName,
                                feeName: feeName,
                                fieldValue: value,
                                fieldFees: price || 0,
                                priceType: getFieldPriceType(field),
                                ...getSessionDynamicPriceFields(field),
                                fieldType: field.field_type,
                                fieldTax: field.is_taxable || '0'
                            });

                            return true; // break loop
                        }
                    });
                }
            } else if (field.field_type === 'checkbox') {
                const isDefault = field.checkbox_default;

                if (isDefault == 1) {

                    let feeName = field.field_label ? field.field_label : 'Checkbox';
                    if (!isOriginal) {
                        feeName += `(${suffix})`;
                    }


                    setFieldValues((prevValues) => ({
                        ...prevValues,
                        [fieldName]: field.field_placeholder || 'yes',
                        [fieldName + '_data']: field,
                    }));


                    if (!repeatPrice && fieldIndex > 1) {
                        return true; // skip adding fee for repeated fields if repeatPrice is false
                    }

                    results.push({
                        fieldName: fieldName,
                        feeName: feeName,
                        fieldValue: field.field_placeholder || 'yes',
                        fieldFees: field.field_price || 0,
                        priceType: getFieldPriceType(field),
                        ...getSessionDynamicPriceFields(field),
                        fieldType: field.field_type,
                        fieldTax: field.is_taxable || '0'
                    });

                }
            }
        });
    });

    return results;
};

// Observe checkout form for checkbox fee label changes
export const observeCheckoutFormForCheckboxFeeLabel = (selector, dispatch) => {

    const targetNode = document.querySelector('.wp-block-woocommerce-checkout-order-summary-fee-block');
    if (!targetNode) return;

    const config = { childList: true, subtree: true };

    const callback = function (mutationsList, observer) {
        for (let mutation of mutationsList) {
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach((addedNode) => {

                    // Check if the added node or its descendants match the selector
                    let labelElements = [];
                    if (addedNode.matches && addedNode.matches('.wc-block-components-totals-fees .wc-block-components-totals-item__label')) {
                        labelElements.push(addedNode);
                    } else if (addedNode.querySelectorAll) {
                        labelElements = Array.from(addedNode.querySelectorAll('.wc-block-components-totals-fees .wc-block-components-totals-item__label'));
                    }

                    labelElements.forEach((labelElement) => {
                        let labelHTML = labelElement.innerHTML;

                        // If label contains an <a ...>...</a> tag, render as HTML (not plain text)
                        if (/<a\s+[^>]*>.*?<\/a>/i.test(labelHTML)) {
                            // If the anchor tag is HTML-encoded, decode it
                            if (labelHTML.includes('&lt;a')) {
                                let newLabelHTML = labelHTML.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
                                if (newLabelHTML !== labelHTML) {
                                    labelElement.innerHTML = newLabelHTML;
                                }
                            }
                            // Already HTML, do nothing (let it render as HTML)
                        } else {
                            labelHTML = labelHTML.replace(/\\/g, '');
                            // Only replace the {link, href="...", link_text="..."} part, not the whole label
                            // Support both straight and curly quotes in the pattern
                            const linkPattern = /\{link,\s*href=["“”]([^"”]+)["“”],\s*link_text=["“”]([^"”]+)["“”]\}/g;
                            let replaced = false;
                            let newLabelHTML = labelHTML.replace(linkPattern, (match, href, linkText) => {
                                replaced = true;
                                return `<a href="${href}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
                            });

                            // Only update the replaced part, leave other text untouched
                            if (replaced) {
                                labelElement.innerHTML = newLabelHTML;
                            } else {
                                // If no link pattern found, escape special characters to prevent HTML injection
                                const div = document.createElement('div');
                                div.textContent = labelHTML;
                                labelElement.innerHTML = div.innerHTML;
                            }
                        }
                    });
                });
            }
        }
    }
    const observer = new MutationObserver(callback);

    observer.observe(targetNode, config);

    // Optionally return the observer to allow disconnection later
    return observer;
};

// Get initial field values with defaults
export const getInitialFieldValues = (customFields) => {

    const initialValues = {};
    customFields.forEach((field) => {
        if (field.is_enable == 0 && field.field_mode == "default") {
            return;
        }
        const fieldName = field.field_name;

        if (field.field_type === 'radio' || field.field_type === 'select' || field.field_type === 'multiselect' || field.field_type === 'checkbox_group') {
            const options = field.options || [];


            options.forEach((option) => {
                const [label, value, price = '', isDefault = ''] = option;

                if (isDefault && value === isDefault) {

                    initialValues[fieldName] = {
                        [fieldName]: value || '',
                        [fieldName + '_data']: field,
                    };

                    return true; // break loop
                } else {
                    initialValues[fieldName] = '';
                }
            });
        } else if (field.field_type === 'checkbox') {
            const isDefault = field.checkbox_default;

            if (isDefault == 1) {
                initialValues[fieldName] = {
                    [fieldName]: field.field_placeholder || '',
                    [fieldName + '_data']: field,
                };

            } else {
                initialValues[fieldName] = '';
            }
        } else if (field.field_type === 'hidden') {
            initialValues[fieldName] = field.field_placeholder || '';
        } else {
            initialValues[fieldName] = '';
        }
    });
    return initialValues;
}


export const getUserMetaValueForField = (field) => {
    const fromField = field?.user_meta_value;
    if (fromField !== undefined && fromField !== null && fromField !== '') {
        return fromField;
    }
    const map = (typeof fme_upload_data !== 'undefined' && fme_upload_data?.user_meta_values) ? fme_upload_data.user_meta_values : {};
    const fromMap = map[field?.field_name];
    if (fromMap !== undefined && fromMap !== null && fromMap !== '') {
        return fromMap;
    }
    return '';
};

export const resolveUserMetaFieldStateValue = (field, fieldName, fieldLabel) => {
    const baseFieldName = String(fieldName).replace(/_\d+$/, '');
    if (fieldName !== baseFieldName) {
        return null;
    }

    const userMetaValue = getUserMetaValueForField(field);
    if (userMetaValue === '' || userMetaValue === null || userMetaValue === undefined) {
        return null;
    }

    const fieldData = { ...field, field_name: fieldName, field_label: fieldLabel };

    if (field.field_type === 'multiselect' || field.field_type === 'checkbox_group' || field.field_type === 'a-multiselect' || field.field_type === 'a-checkbox_group') {
        const keys = String(userMetaValue).split(',').map((item) => item.trim()).filter(Boolean);
        if (!keys.length) {
            return null;
        }
        return {
            value: keys.map((key) => {
                const opt = (field.options || []).find((option) => String(option[1]) === String(key));
                return { key, value: opt ? Number(opt[2] || 0) : 0 };
            }),
            data: fieldData,
        };
    }

    if (field.field_type === 'date_picker') {
        const formattedDate = formatStoredDateForDatepicker(field, userMetaValue);
        if (!formattedDate) {
            return null;
        }
        return {
            value: formattedDate,
            data: fieldData,
        };
    }

    return {
        value: userMetaValue,
        data: fieldData,
    };
};

export const mergeUserMetaIntoFieldValues = (customFields, fieldValues) => {
    if (!Array.isArray(customFields) || !fieldValues) {
        return fieldValues;
    }

    const next = { ...fieldValues };

    customFields.forEach((field) => {
        if (field.is_enable == 0 || field.field_mode === 'default') {
            return;
        }

        const fieldName = field.field_name;
        const meta = resolveUserMetaFieldStateValue(field, fieldName, field.field_label);
        if (!meta) {
            return;
        }

        const current = next[fieldName];
        const isEmpty = current === undefined || current === null || current === '' || current === '---';
        const shouldOverrideDatePickerDefault = field.field_type === 'date_picker' && !isEmpty;

        if (!isEmpty && !shouldOverrideDatePickerDefault) {
            return;
        }

        if (shouldOverrideDatePickerDefault) {
            const formattedCurrent = formatStoredDateForDatepicker(field, current);
            if (formattedCurrent === meta.value) {
                return;
            }
        }

        next[fieldName] = meta.value;
        next[fieldName + '_data'] = meta.data;
    });

    return next;
};

export const applyUserMetaPrefillToFieldState = (fields, fieldValues) => {
    if (!Array.isArray(fields) || !fieldValues) {
        return fieldValues;
    }

    const eligible = fields.filter((field) => !field.isHide && field.is_enable != 0 && field.field_mode !== 'default');
    const next = mergeUserMetaIntoFieldValues(eligible, fieldValues);
    const changed = eligible.some((field) => next[field.field_name] !== fieldValues[field.field_name]);

    return changed ? next : fieldValues;
};

const applyUserMetaInitialValue = (field, fieldName, fieldLabel, initialValues, isOriginal) => {
    if (!isOriginal) {
        return false;
    }

    const meta = resolveUserMetaFieldStateValue(field, fieldName, fieldLabel);
    if (!meta) {
        return false;
    }

    initialValues[fieldName] = meta.value;
    initialValues[fieldName + '_data'] = meta.data;
    return true;
};

export const isPercentagePriceType = (priceType) => (
    ['percent_cart_total', 'percent_subtotal', 'percent_subtotal_ex_tax'].includes(priceType || 'fixed')
);

export const isDynamicPriceType = (priceType) => (priceType || 'fixed') === 'dynamic';

export const getDynamicUnitsPerPrice = (field) => {
    const value = field?.dynamic_units_per_price ?? field?.dynamicUnitsPerPrice ?? '1';
    return Math.max(1, parseInt(value, 10) || 1);
};

export const getPriceUnitLabel = (field) => {
    const value = field?.price_unit_label ?? field?.priceUnitLabel ?? '';
    const label = String(value).trim();
    return label !== '' ? label : 'item';
};

export const getSessionDynamicPriceFields = (field) => ({
    dynamicUnitsPerPrice: String(getDynamicUnitsPerPrice(field)),
    priceUnitLabel: getPriceUnitLabel(field),
});

const percentagePriceSuffixes = {
    percent_cart_total: 'of cart total',
    percent_subtotal: 'of subtotal',
    percent_subtotal_ex_tax: 'of subtotal (ex. tax)',
};

export const formatFieldPriceDisplay = (amount, priceType, currencySymbol = '', field = null) => {
    const num = Number(amount || 0);
    if (!num) {
        return '';
    }
    if (isPercentagePriceType(priceType)) {
        const suffix = percentagePriceSuffixes[priceType] || '';
        return suffix ? ` (${num}% ${suffix})` : ` (${num}%)`;
    }
    if (isDynamicPriceType(priceType) && field) {
        const units = getDynamicUnitsPerPrice(field);
        const label = getPriceUnitLabel(field);
        return ` (${currencySymbol} ${num} per ${units} ${label})`;
    }
    return ` (${currencySymbol} ${num})`;
};

export const formatOptionPriceDisplay = (amount, field, currencySymbol = '') => {
    if (!amount && amount !== 0) {
        return '';
    }

    return formatFieldPriceDisplay(amount, getFieldPriceType(field), currencySymbol, field);
};

export const getFieldPriceType = (field) => field?.price_type || 'fixed';

const buildSessionFeeEntry = (fieldId, displayLabel, fieldValue, fieldFees, field) => ({
    fieldId,
    fieldName: displayLabel,
    feeName: displayLabel,
    fieldValue,
    fieldFees,
    priceType: getFieldPriceType(field),
    ...getSessionDynamicPriceFields(field),
    fieldType: field.field_type,
    fieldTax: field.is_taxable || '0',
});

const buildFeeEntryForFieldValue = (field, fieldName, fieldLabel, fieldValue, suffix, repeatPrice, fieldIndex) => {
    if (!repeatPrice && fieldIndex > 1) {
        return null;
    }
    if (fieldValue === '' || fieldValue === null || fieldValue === undefined || fieldValue === '---') {
        return null;
    }

    let feeName = field.field_label ? field.field_label : 'Field';
    if (suffix) {
        feeName += suffix;
    }

    const optionTypes = ['radio', 'select', 'a-radio', 'a-select'];
    const multiTypes = ['multiselect', 'checkbox_group', 'a-multiselect', 'a-checkbox_group'];

    if (optionTypes.includes(field.field_type)) {
        const options = field.options || [];
        const match = options.find((option) => String(option[1]) === String(fieldValue));
        if (!match) {
            return null;
        }
        const price = Number(match[2] || 0);
        const optionLabel = match[0] || fieldValue;
        const sessionLabel = (field.field_type === 'radio' || field.field_type === 'a-radio') && field.field_label
            ? `${field.field_label} - ${optionLabel}`
            : optionLabel;
        return buildSessionFeeEntry(fieldName, sessionLabel, fieldValue, price, field);
    }

    if (multiTypes.includes(field.field_type)) {
        let selections = fieldValue;
        if (!Array.isArray(selections)) {
            const keys = String(fieldValue).split(',').map((item) => item.trim()).filter(Boolean);
            selections = keys.map((key) => {
                const opt = (field.options || []).find((option) => String(option[1]) === String(key));
                return { key, value: opt ? Number(opt[2] || 0) : 0 };
            });
        }
        if (!selections.length) {
            return null;
        }
        return buildSessionFeeEntry(
            fieldName,
            feeName,
            selections,
            selections.reduce((sum, item) => sum + Number(item.value || 0), 0),
            field
        );
    }

    if (field.field_type === 'checkbox' || field.field_type === 'a-checkbox') {
        if (!fieldValue) {
            return null;
        }
        return buildSessionFeeEntry(fieldName, feeName, fieldValue, Number(field.field_price || 0), field);
    }

    const fieldPrice = Number(field.field_price || 0);
    if (!fieldPrice) {
        return null;
    }

    return buildSessionFeeEntry(fieldName, feeName, fieldValue, fieldPrice, field);
};

export const setInitialFieldData = (customFields, fieldValues, setFieldValues, cartData) => {

    let initialData = {};
    let initialErrors = {};
    let initialValues = {};
    let feeData = [];
    let repeatField = [];

    customFields.forEach((field) => {

        if (field.is_enable == 0 || field.isHide === true || field.field_mode == "default") {
            return;
        }

        // Handle field repetition
        const shouldRepeat = field.repeat_rules?.fme_is_repeat == 'on';
        let repeatCount = shouldRepeat ? getRepeatCount(cartData, field.repeat_rules) : 1;

        if (0 == repeatCount) {
            repeatCount = 1;
        }

        [...Array(repeatCount)].forEach((_, index) => {
            const isOriginal = index === 0;
            const fieldIndex = index + 1;

            let isRepeatPrice = field.repeat_rules.fme_is_repeat == 'on' && field.repeat_rules.fme_repeat_price != 'on';
            let repeatPrice = !isOriginal && isRepeatPrice ? false : true;

            const suffix = field.repeat_rules?.fme_repeat_label_suffix === 'alphabet'
                ? String.fromCharCode(65 + index)
                : (fieldIndex).toString();
            const fieldName = isOriginal ? field.field_name : `${field.field_name}_${fieldIndex}`;
            const fieldLabel = isOriginal ? field.field_label : `${field.field_label} (${suffix})`;

            // Append all fields plus repeated ones
            repeatField.push({ ...field, field_name: fieldName, field_label: fieldLabel });

            // Set initial errors
            if (field.field_type === 'hidden') {
                initialErrors[fieldName] = false;
                initialErrors[fieldName + '_error_label'] = (field.field_label || 'Field') + ' requires a default value.';
            } else if (field.is_required == 1) {
                let customErrorMessage = field.required_error_message ? field.required_error_message : '';
                initialErrors[fieldName] = false;
                initialErrors[fieldName + '_error_label'] = customErrorMessage || (field.field_type === 'checkbox'
                    ? getCheckboxLabelText(fieldLabel, isOriginal ? '' : `(${suffix})`)
                    : fieldLabel) + ' is required.';
            }

            // set initial values — user meta first, then field defaults
            const optionFieldTypes = ['radio', 'select', 'multiselect', 'checkbox_group', 'a-radio', 'a-select', 'a-multiselect', 'a-checkbox_group'];
            const checkboxFieldTypes = ['checkbox', 'a-checkbox'];

            if (optionFieldTypes.includes(field.field_type)) {
                if (!applyUserMetaInitialValue(field, fieldName, fieldLabel, initialValues, isOriginal)) {
                    const options = field.options || [];
                    options.some((option) => {
                        const [label, value, price = '', isDefault = ''] = option;

                        if (isDefault !== '' && value === isDefault) {
                            initialValues[fieldName] = value || '';
                            initialValues[fieldName + '_data'] = { ...field, field_name: fieldName, field_label: fieldLabel };
                            return true;
                        }
                        initialValues[fieldName] = '';
                        return false;
                    });
                }
            } else if (checkboxFieldTypes.includes(field.field_type)) {
                if (!applyUserMetaInitialValue(field, fieldName, fieldLabel, initialValues, isOriginal)) {
                    const isDefault = field.checkbox_default;
                    if (isDefault == 1) {
                        initialValues[fieldName] = field.field_placeholder || '';
                        initialValues[fieldName + '_data'] = { ...field, field_name: fieldName, field_label: fieldLabel };
                    } else {
                        initialValues[fieldName] = '';
                    }
                }
            } else if (field.field_type === 'hidden') {
                if (!applyUserMetaInitialValue(field, fieldName, fieldLabel, initialValues, isOriginal)) {
                    initialValues[fieldName] = field.field_placeholder || '';
                    initialValues[fieldName + '_data'] = { ...field, field_name: fieldName, field_label: fieldLabel };
                }
            } else if (field.field_type === 'heading' || field.field_type === 'label' || field.field_type === 'paragraph') {
                initialValues[fieldName] = '1';
                initialValues[fieldName + '_data'] = { ...field, field_name: fieldName, field_label: fieldLabel };
            } else if (field.field_type === 'date_picker') {
                if (!applyUserMetaInitialValue(field, fieldName, fieldLabel, initialValues, isOriginal)) {
                    initialValues[fieldName] = resolveDefaultDateFromField(field) || '';
                    initialValues[fieldName + '_data'] = { ...field, field_name: fieldName, field_label: fieldLabel };
                }
            } else if (!applyUserMetaInitialValue(field, fieldName, fieldLabel, initialValues, isOriginal)) {
                initialValues[fieldName] = '';
                initialValues[fieldName + '_data'] = { ...field, field_name: fieldName, field_label: fieldLabel };
            }

            const suffixLabel = isOriginal ? '' : `(${suffix})`;
            const feeEntry = buildFeeEntryForFieldValue(
                field,
                fieldName,
                fieldLabel,
                initialValues[fieldName],
                suffixLabel,
                repeatPrice,
                fieldIndex
            );
            if (feeEntry && true !== field.isHide) {
                feeData.push(feeEntry);
            }

        });
    });

    initialData['initialErrors'] = initialErrors;
    initialData['initialValues'] = initialValues;
    initialData['feeData'] = feeData;
    initialData['repeatField'] = repeatField;

    return initialData;
};

export const isFieldHiddenForValidation = (fieldKey, fieldValues, fields) => {
    if (fieldValues[fieldKey] === '---') {
        return true;
    }

    const fieldData = fieldValues[fieldKey + '_data'];
    if (fieldData?.isHide === true) {
        return true;
    }

    const baseFieldName = fieldKey.replace(/_\d+$/, '');
    const field = fields.find((f) => f.field_name === fieldKey || f.field_name === baseFieldName);
    return field?.isHide === true;
};

export const isTimePickerEffectivelyRequired = (field, fieldKey, fieldValues) => {
    if (!field || field.field_type !== 'time_picker' || field.is_required != 1) {
        return false;
    }

    const config = getTimepickerConfig(field);
    const linkedDateValue = resolveLinkedDateValue(config.linkedDate, fieldKey, field.field_name, fieldValues);
    return generateTimeSlots(config, linkedDateValue).length > 0;
};

export const isFieldValueEmptyForValidation = (value, field) => {
    if (value === '---' || value === null || value === undefined || value === '') {
        return true;
    }

    if (Array.isArray(value) && value.length === 0) {
        return true;
    }

    if (typeof value === 'string' && (value === '[]' || value === '0')) {
        return true;
    }

    if ((field?.field_type === 'checkbox_group' || field?.field_type === 'multiselect') && typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed) && parsed.length === 0) {
                return true;
            }
        } catch (e) {
            // Keep the original value when it is not JSON.
        }
    }

    return false;
};

// Show required fields error - set the first empty required field's error to true - called on fieldValues change.
// Does not re-validate masks on every keystroke (blur handles that); preserves blur-set format errors.
export const showRequiredFieldsError = (fieldValues, errors, setErrors, fields) => {
    const decorativeFieldTypes = ['label', 'heading', 'paragraph'];

    for (const field of fields) {
        const fieldKey = field.field_name;
        const value = fieldValues[fieldKey];

        if (isFieldHiddenForValidation(fieldKey, fieldValues, fields)) {
            if (errors[fieldKey]) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [fieldKey]: false,
                }));
            }
            continue;
        }

        if (field.field_type === 'hidden') {
            const defaultValue = String(field.field_placeholder || '').trim();
            const rawValue = value;
            const submittedValue = typeof rawValue === 'string'
                ? rawValue.trim()
                : (rawValue !== undefined && rawValue !== null ? String(rawValue).trim() : '');

            // Configured default is enough — empty / "0" submission is filled from default elsewhere.
            if (defaultValue) {
                if (errors[fieldKey]) {
                    setErrors((prevErrors) => ({
                        ...prevErrors,
                        [fieldKey]: false,
                    }));
                }
                continue;
            }

            if (!submittedValue || submittedValue === '0') {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [fieldKey]: true,
                    [fieldKey + '_error_label']: (field.field_label || 'Field') + ' requires a default value.',
                }));

                let editAddress = document.querySelector('.wc-block-components-address-card__edit');
                if (editAddress) {
                    editAddress.click();
                }

                break;
            }

            if (errors[fieldKey]) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [fieldKey]: false,
                }));
            }
            continue;
        }

        if (field.is_required != 1 || decorativeFieldTypes.includes(field.field_type)) {
            if (errors[fieldKey]) {
                // Keep blur-set mask/format errors for non-empty values.
                if (
                    !decorativeFieldTypes.includes(field.field_type)
                    && !isFieldValueEmptyForValidation(value, field)
                ) {
                    const formatResult = validateFieldFormatRules(value, field, fieldValues);
                    if (!formatResult.valid) {
                        continue;
                    }
                }
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [fieldKey]: false,
                }));
            }
            continue;
        }

        if (field.field_type === 'time_picker' && !isTimePickerEffectivelyRequired(field, fieldKey, fieldValues)) {
            if (errors[fieldKey]) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [fieldKey]: false,
                }));
            }
            continue;
        }

        if (isFieldValueEmptyForValidation(value, field)) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                [fieldKey]: true
            }));

            let editAddress = document.querySelector('.wc-block-components-address-card__edit');
            if (editAddress) {
                editAddress.click();
            }

            break; // break loop if one error is set to true
        }

        // Required + filled: preserve blur-set mask/format errors (do not clear on keystroke).
        if (errors[fieldKey] && !isFieldValueEmptyForValidation(value, field)) {
            const formatResult = validateFieldFormatRules(value, field, fieldValues);
            if (!formatResult.valid) {
                continue;
            }
            setErrors((prevErrors) => ({
                ...prevErrors,
                [fieldKey]: false,
                [fieldKey + '_error_label']: undefined,
            }));
        }
    }

}


export const setBulkFieldsValuesInSession = (customFields, fieldValues, setFieldValues, cartDispatch) => {

    let results = checkedDefaultOptionsOnLoad(customFields, fieldValues, setFieldValues);

    if (results.length === 0) {
        return;
    }
    // Now send to PHP via AJAX
    jQuery.ajax({
        url: fme_upload_data.ajax_url,
        type: 'POST',
        data: {
            action: 'fme_store_fields_value_in_session_on_load',
            feeData: results,
            security: fme_upload_data.upload_nonce
        },
        success: function (response) {
            if (response.success) {
            } else {
                console.warn(`Failed to store:`, response.data);
            }
        },
        error: function (xhr, status, error) {
            console.log('Response:', xhr.responseText);
        }
    });

}

export const hasUserMetaPrefillValues = (customFields) => {
    if (!Array.isArray(customFields) || !customFields.length) {
        return false;
    }
    return customFields.some((field) => {
        const userMetaValue = getUserMetaValueForField(field);
        return userMetaValue !== '' && userMetaValue !== null && userMetaValue !== undefined;
    });
};

const triggerPrefillFeeCartUpdate = (feeData, cartDispatch) => {
    const dispatch = cartDispatch || wp.data.dispatch('wc/store/cart');
    if (!dispatch?.applyExtensionCartUpdate) {
        return false;
    }

    dispatch.applyExtensionCartUpdate({
        namespace: 'fme-ccfw-fee-update',
        data: {
            trigger: 'prefill_fees_updated',
            feeData,
            bulkSingleUpdate: 'bulk',
        },
    });

    return true;
};

export const saveDefaultFieldValuesInSession = (feeData, cartDispatch, delay = 0) => {
    if (!feeData?.length) {
        return;
    }

    const hash = JSON.stringify(feeData);
    const zeroFeeCount = feeData.filter(
        (item) => Number(item.fieldFees) === 0
    ).length;

    if (sentHashes.current[zeroFeeCount] === hash) {
        return;
    }

    sentHashes.current[zeroFeeCount] = hash;

    if (debounceTimers.current[zeroFeeCount]) {
        clearTimeout(debounceTimers.current[zeroFeeCount]);
    }

    const syncFees = () => {
        if (triggerPrefillFeeCartUpdate(feeData, cartDispatch)) {
            return;
        }

        jQuery.ajax({
            url: fme_upload_data.ajax_url,
            type: 'POST',
            data: {
                action: 'fme_store_fields_value_in_session_on_load',
                feeData,
                security: fme_upload_data.upload_nonce,
            },
            success(response) {
                if (response.success) {
                    triggerPrefillFeeCartUpdate(feeData, cartDispatch);
                }
            },
        });
    };

    debounceTimers.current[zeroFeeCount] = setTimeout(syncFees, delay > 0 ? delay : 150);
};

// Get repeat count based on rules
export const getRepeatCount = (cart, repeatRules) => {

    const basedOn = repeatRules.fme_repeat_based_on || 'cart_count';
    const selectedIds = (repeatRules.fme_repeat_for_products || []).map(Number);

    if (basedOn === 'cart_count') {
        return cart.items.reduce((total, item) => total + item.quantity, 0);
    }

    if (basedOn === 'product_qty') {
        return cart.items.reduce((total, item) => {

            const itemIds = getCartItemProductIds(item);
            const isMatch = selectedIds.some((id) => itemIds.includes(id));
            return isMatch ? total + item.quantity : total;
        }, 0);
    }

    return 1;

}

// If a field is hidden via conditions but has a default value, we need to update its value to '---' in session so that fees are not added based on default values of hidden fields. And if the field becomes visible again, we can set the default value back (if it was '---') or keep the user entered value if they changed it before hiding.
export const updateFieldValuesForHiddenFields = (fieldValues, fields, cartData) => {

    fields.forEach((field) => {

        if (field.is_enable == 0 || field.field_mode == "default") {
            return;
        }

        // Handle field repetition
        const shouldRepeat = field.repeat_rules?.fme_is_repeat == 'on';
        let repeatCount = shouldRepeat ? getRepeatCount(cart, field.repeat_rules) : 1;

        if (0 == repeatCount) {
            repeatCount = 1;
        }

        [...Array(repeatCount)].forEach((_, index) => {
            const isOriginal = index === 0;
            const fieldIndex = index + 1;

            let isRepeatPrice = field.repeat_rules.fme_is_repeat == 'on' && field.repeat_rules.fme_repeat_price != 'on';
            let repeatPrice = !isOriginal && isRepeatPrice ? false : true;

            const suffix = field.repeat_rules?.fme_repeat_label_suffix === 'alphabet'
                ? String.fromCharCode(65 + index)
                : (fieldIndex).toString();
            const fieldName = isOriginal ? field.field_name : `${field.field_name}_${fieldIndex}`;
            const fieldLabel = isOriginal ? field.field_label : `${field.field_label} (${suffix})`;


            if (field.isHide) {
                if (fieldValues[fieldName] !== undefined && fieldValues[fieldName] !== '' && fieldValues[fieldName] !== '---') {
                    fieldValues[fieldName + '_fme_prefill'] = fieldValues[fieldName];
                }
                fieldValues[fieldName] = '---';
                fieldValues[fieldName + '_data'] = { ...field, isHide: true, field_label: fieldLabel, field_name: fieldName }; // keep the field data for later use when it becomes visible again
            } else if (!field.isHide && fieldValues[fieldName] === '---') {
                const meta = resolveUserMetaFieldStateValue(field, fieldName, fieldLabel);
                fieldValues[fieldName] = fieldValues[fieldName + '_fme_prefill']
                    ?? (meta ? meta.value : '');
                fieldValues[fieldName + '_data'] = {
                    ...(meta?.data || fieldValues[fieldName + '_data'] || field),
                    isHide: false,
                    field_label: fieldLabel,
                    field_name: fieldName,
                };
            }

        });
    });

    return fieldValues;
}


const mapBulkFeeEntry = (fieldId, entry) => {
    if (!entry) {
        return null;
    }

    return {
        fieldName: fieldId,
        feeName: entry.feeName,
        fieldValue: entry.fieldValue,
        fieldFees: entry.fieldFees,
        priceType: entry.priceType,
        dynamicUnitsPerPrice: entry.dynamicUnitsPerPrice,
        priceUnitLabel: entry.priceUnitLabel,
        fieldType: entry.fieldType,
        fieldTax: entry.fieldTax,
    };
};

export const getUpdatedFieldsFeeDataInSession = (fields, fieldValues, cartData, cartDispatch, restoredFieldNames = null) => {
    const feeData = [];
    const restoredSet = restoredFieldNames ? new Set(restoredFieldNames) : null;

    fields.forEach((field) => {
        if (field.is_enable == 0 || field.field_mode == 'default' || field.isHide === true) {
            return;
        }

        const shouldRepeat = field.repeat_rules?.fme_is_repeat == 'on';
        let repeatCount = shouldRepeat ? getRepeatCount(cartData, field.repeat_rules) : 1;

        if (0 == repeatCount) {
            repeatCount = 1;
        }

        [...Array(repeatCount)].forEach((_, index) => {
            const isOriginal = index === 0;
            const fieldIndex = index + 1;

            const isRepeatPrice = field.repeat_rules.fme_is_repeat == 'on' && field.repeat_rules.fme_repeat_price != 'on';
            const repeatPrice = !isOriginal && isRepeatPrice ? false : true;

            const suffix = field.repeat_rules?.fme_repeat_label_suffix === 'alphabet'
                ? String.fromCharCode(65 + index)
                : (fieldIndex).toString();
            const fieldName = isOriginal ? field.field_name : `${field.field_name}_${fieldIndex}`;
            const fieldLabel = isOriginal ? field.field_label : `${field.field_label} (${suffix})`;

            if (restoredSet && !restoredSet.has(fieldName) && !restoredSet.has(field.field_name)) {
                return;
            }

            const suffixLabel = isOriginal ? '' : `(${suffix})`;
            const currentValue = fieldValues[fieldName];
            const entry = buildFeeEntryForFieldValue(
                field,
                fieldName,
                fieldLabel,
                currentValue,
                suffixLabel,
                repeatPrice,
                fieldIndex
            );

            const bulkEntry = mapBulkFeeEntry(fieldName, entry);
            if (bulkEntry) {
                feeData.push(bulkEntry);
            }
        });
    });

    return feeData;
};

/**
 * Build zero-fee bulk entries for fields currently hidden via conditional logic.
 * Fee restoration on re-show is handled separately by applyRestoredFieldFees().
 */
export const buildHiddenFieldsFeeData = ({
    fieldsNewValues,
    hiddenFieldsRef,
    lastSentValues,
}) => {
    const feeData = [];

    for (const [key, value] of Object.entries(fieldsNewValues)) {
        if (value !== '---' || key.endsWith('_data') || key.endsWith('_fme_prefill')) {
            continue;
        }

        hiddenFieldsRef.current[key] = true;
        lastSentValues.current[key] = '';

        const fieldData = fieldsNewValues[key + '_data'];
        if (!fieldData) {
            continue;
        }

        feeData.push({
            fieldName: key,
            feeName: fieldData?.field_label || '',
            fieldValue: '',
            fieldFees: 0,
            fieldType: fieldData?.field_type,
            fieldTax: fieldData?.is_taxable || '0',
        });
    }

    return feeData;
};

/**
 * Re-apply fees when a conditionally hidden field becomes visible again.
 * Uses per-field single updates (same path as handleChange) so debounced bulk
 * restore attempts are not cancelled by the follow-up render.
 */
export const applyRestoredFieldFees = (restoredFields, cartDispatch, lastSentValues) => {
    if (!Array.isArray(restoredFields) || restoredFields.length === 0) {
        return;
    }

    const applyUpdate = cartDispatch?.applyExtensionCartUpdate;
    if (!applyUpdate) {
        return;
    }

    restoredFields.forEach(({ fieldName, value, field, fieldLabel, fieldIndex = 1, repeatPrice = true }) => {
        if (!field || value === '---' || value === '' || value === null || value === undefined) {
            return;
        }

        if (lastSentValues?.current) {
            delete lastSentValues.current[fieldName];
        }

        const suffixLabel = fieldIndex > 1
            ? (field.repeat_rules?.fme_repeat_label_suffix === 'alphabet'
                ? `(${String.fromCharCode(64 + fieldIndex)})`
                : `(${fieldIndex})`)
            : '';

        const entry = buildFeeEntryForFieldValue(
            field,
            fieldName,
            fieldLabel || field.field_label || '',
            value,
            suffixLabel,
            repeatPrice,
            fieldIndex
        );

        if (!entry) {
            return;
        }

        applyUpdate({
            namespace: 'fme-ccfw-fee-update',
            data: {
                trigger: 'fee_updated',
                fieldId: fieldName,
                fieldValue: {
                    fieldName: entry.feeName,
                    fieldValue: entry.fieldValue,
                    fieldFees: entry.fieldFees,
                    priceType: entry.priceType,
                    dynamicUnitsPerPrice: entry.dynamicUnitsPerPrice,
                    priceUnitLabel: entry.priceUnitLabel,
                    fieldType: entry.fieldType,
                    fieldTax: entry.fieldTax,
                },
                bulkSingleUpdate: 'single',
            },
        });

        if (lastSentValues?.current) {
            lastSentValues.current[fieldName] = Array.isArray(value) ? JSON.stringify(value) : value;
        }
    });
};